export interface PointerData {
    pointerID: number;
    lastEvent: PointerEvent;
    isCapturing: boolean;
    isPointerDown: boolean;
    lastDownTime?: number;
    captureTarget?: HTMLElement;
    pointerType: PointerType;
    lastEventType: "pointerdown" | "pointerup" | "pointermove" | "pointerover" | "pointerout" | "pointerenter" | "pointerleave" | "pointercancel";

    // internal stuff
    firefoxPenTouch: boolean;

    // API consumer has free reign over this
    metadata: any;
}

export type PointerType = "mouse" | "pen" | "touch";
export type PointerEventCallback = (e: PointerEvent,data: PointerData) => void;
// export type CSSTouchAction = "none" | "auto" | "pan-x" | "pan-left" | "pan-right" | "pan-y" | "pan-up" | "pan-down" | "pinch-zoom" | "manipulation";
export type CSSTouchAction = "none" | "auto" | "pan-x" | "pan-y" | "pinch-zoom" | "manipulation";
export type PointerEventMask = {
    pointerdown?: boolean;
    pointerup?: boolean;
    pointermove?: boolean;
    pointerover?: boolean;
    pointerout?: boolean;
    pointerenter?: boolean;
    pointerleave?: boolean;
    pointercancel?: boolean;
}

export class PointerEventManager {
    pointers: Map<PointerData["pointerID"],PointerData> = new Map();

    pointerDownEvents: PointerEventCallback[] = [];
    pointerDoubleDownEvents: PointerEventCallback[] = [];
    pointerUpEvents: PointerEventCallback[] = [];
    pointerMoveEvents: PointerEventCallback[] = [];
    pointerOverEvents: PointerEventCallback[] = [];
    pointerOutEvents: PointerEventCallback[] = [];
    pointerEnterEvents: PointerEventCallback[] = [];
    pointerLeaveEvents: PointerEventCallback[] = [];
    pointerCancelEvents: PointerEventCallback[] = [];

    isMouseDown = true;

    constructor(public element: HTMLElement,public wantedEvents: PointerEventMask,touchAction?: CSSTouchAction[],public passive?: PointerEventMask,public strict?: boolean) {
        this.setTouchAction(touchAction);

        this.element.addEventListener("pointerdown",(e) => {
            if(!this.shouldProcessEvent(e)) {return;}
            let data = this.registerPointer(e);

            data.isPointerDown = true;

            for(let cb of this.pointerDownEvents) {
                cb(e,data);
            }
        },{passive: passive.pointerdown})

        this.element.addEventListener("pointerup",(e) => {
            if(!this.shouldProcessEvent(e)) {return;}
            let data = this.registerPointer(e);

            let now = Date.now();

            let doubleClick = false;

            if(data.lastDownTime) {
                if((now - data.lastDownTime) < 250) {
                    // double click

                    doubleClick = true;
                }
            }

            data.lastDownTime = Date.now();
            data.isPointerDown = false;

            if(doubleClick) {
                for(let cb of this.pointerDoubleDownEvents) {
                    cb(e,data);
                }
            }

            for(let cb of this.pointerUpEvents) {
                cb(e,data);
            }
        },{passive: passive.pointerup})

        if(wantedEvents.pointermove) {
            this.element.addEventListener("pointermove",(e) => {
                if(!this.shouldProcessEvent(e)) {return;}
                let data = this.registerPointer(e);

                for(let cb of this.pointerMoveEvents) {
                    cb(e,data);
                }
            },{passive: passive.pointermove})
        }

        if(wantedEvents.pointerover) {
            this.element.addEventListener("pointerover",(e) => {
                if(!this.shouldProcessEvent(e)) {return;}
                let data = this.registerPointer(e);

                for(let cb of this.pointerOverEvents) {
                    cb(e,data);
                }
            },{passive: passive.pointerover})
        }

        if(wantedEvents.pointerout) {
            this.element.addEventListener("pointerout",(e) => {
                if(!this.shouldProcessEvent(e)) {return;}
                let data = this.registerPointer(e);

                for(let cb of this.pointerOutEvents) {
                    cb(e,data);
                }
            },{passive: passive.pointerout})
        }

        if(wantedEvents.pointerenter) {
            this.element.addEventListener("pointerenter",(e) => {
                if(!this.shouldProcessEvent(e)) {return;}
                let data = this.registerPointer(e);

                for(let cb of this.pointerEnterEvents) {
                    cb(e,data);
                }
            },{passive: passive.pointerenter})
        }

        if(wantedEvents.pointerleave) {
            this.element.addEventListener("pointerleave",(e) => {
                if(!this.shouldProcessEvent(e)) {return;}
                let data = this.registerPointer(e);

                for(let cb of this.pointerLeaveEvents) {
                    cb(e,data);
                }

                if(!data.isCapturing) {
                    if(e.target === this.element) {
                        this.deregisterPointer(e);

                        for(let cb of this.pointerCancelEvents) {
                            cb(e,data);
                        }
                    }
                }
            },{passive: passive.pointerleave})
        }

        this.element.addEventListener("pointercancel",(e) => {
            if(!this.shouldProcessEvent(e)) {return;}
            let data = this.registerPointer(e);

            // TODO: remove this monkey patch for firefox pen shenanigans
            // if(navigator.userAgent.indexOf("Firefox")) {
            //     if(data.pointerType === "pen") {
            //         if(!data.firefoxPenTouch) {
            //             this.deregisterPointer(e);
            //         }
            //         else {
            //             data.firefoxPenTouch = false;
            //             return;
            //         }
            //     }
            //     else {
            //         this.deregisterPointer(e);
            //     }
            // }
            // else {
            //     this.deregisterPointer(e);
            // }

            this.deregisterPointer(e);

            for(let cb of this.pointerCancelEvents) {
                cb(e,data);
            }
        },{passive: passive.pointercancel})
    }

    setTouchAction(touchAction?: CSSTouchAction[]) {
        if(touchAction) {
            this.element.setAttribute("touch-action",touchAction.join(" "));
        }
        else {
            this.element.setAttribute("touch-action","");
        }
    }

    shouldProcessEvent(e: PointerEvent) {
        if(e.type === "pointerup") {
            let data = this.pointers.get(e.pointerId);

            if(data) {
                if(!data.isPointerDown) {
                    return false;
                }
            }
            else {
                return false;
            }
        }
        else if(e.type === "pointerenter") {return true;}
        else if(e.type === "pointerleave") {return true;}
        else if(e.type === "pointerover") {return true;}
        else if(e.type === "pointerout") {return true;}
        else if(e.type === "pointercancel") {return true;}

        if(this.strict) {
            if(this.element !== e.srcElement) {
                if(!this.element.contains(e.srcElement)) {
                    return false;
                }
            }
        }

        return true;
    }

    registerPointer(e: PointerEvent) {
        let data = this.pointers.get(e.pointerId);

        if(e.pointerType == "mouse") {
            this.isMouseDown = (e.buttons & 1) === 1;
        }

        if(data) {
            data.lastEvent = e;
            data.lastEventType = e.type as any;

            if(data.pointerType !== "pen") {
                data.pointerType = e.pointerType as any;
            }
            else if(e.pointerType !== "pen") {
                data.firefoxPenTouch = true;
            }
        }
        else {
            data = {
                pointerID: e.pointerId,
                lastEvent: e,
                isCapturing: false,
                isPointerDown: false,
                lastEventType: e.type as any,
                pointerType: e.pointerType as any,
                metadata: {},
                firefoxPenTouch: false,
            }

            this.pointers.set(e.pointerId,data);
        }

        return data;
    }

    deregisterPointer(e: PointerEvent) {
        let data = this.pointers.get(e.pointerId);

        if(data) {
            data.lastEvent = e;
            data.lastEventType = "pointercancel";
            data.isPointerDown = false;
            data.isCapturing = false;
            delete data.captureTarget;
        }

        this.pointers.delete(e.pointerId);
    }

    capturePointer(pointers: PointerEvent[],el?: HTMLElement) {
        for(let pointer of pointers) {
            let data = this.pointers.get(pointer.pointerId);

            if(data) {
                data.isCapturing = true;
                data.captureTarget = el || this.element;
            }

            try {
                if(el) {
                    el.setPointerCapture(pointer.pointerId);
                }
                else {
                    this.element.setPointerCapture(pointer.pointerId);
                }
            }
            catch(e) {
                if(e instanceof Error) {
                    if(e.name === "InvalidPointerId") {
                        // ignore
                    }
                    else {
                        throw e;
                    }
                }
                else {
                    throw e;
                }
            }
        }
    }

    releasePointer(pointers: PointerEvent[],el?: HTMLElement) {
        for(let pointer of pointers) {
            let data = this.pointers.get(pointer.pointerId);

            if(data) {
                data.isCapturing = false;
                delete data.captureTarget;
            }

            try {
                if(el) {
                    el.releasePointerCapture(pointer.pointerId);
                }
                else {
                    this.element.releasePointerCapture(pointer.pointerId);
                }
            }
            catch(e) {
                if(e instanceof Error) {
                    if(e.name === "InvalidPointerId") {
                        // ignore
                    }
                    else {
                        throw e;
                    }
                }
                else {
                    throw e;
                }
            }
        }
    }

    getDownPointers(pointerType?: PointerType) {
        let out: PointerEvent[] = [];

        for(let iter of this.pointers) {
            if(iter[1].isPointerDown) {
                if(pointerType) {
                    if(iter[1].lastEvent.pointerType !== pointerType) {continue;}
                }

                out.push(iter[1].lastEvent);
            }
        }

        return out;
    }
}
